import { Component, inject, OnDestroy } from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Validators } from '@angular/forms';
import {
	LabelsHistoricoVariaveisMeteorologicas,
	TiposPeriodosVariaveisMeteorologicas,
	VariaveisMeteorologicasPorPeriodo,
	VariaveisMeteorologicasPorPeriodoETipoVariavel,
} from '@home/submodulos/dados-meteorologicos/interfaces/variavel-meteorologica-mapa';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import { ValuesVariaveis } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/interfaces/estacao-monitorada';
import { LegendaOptions } from '@home/submodulos/dados-meteorologicos/componentes/legenda-relatorio-historico-variaveis/legenda-relatorio-historico-variaveis.component';
import {
	alinharDado,
	baixarEAbrirPDF,
	getBase64ImageFromURL,
	isNotNuloOuUndefined,
	isNuloOuUndefined,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import * as pdfseira from '@utils/pdf-seira';
import html2canvas from 'html2canvas';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment/moment';
import { Moment } from 'moment';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { DateTimeUtils } from '@utils/datetime-util';
import {
	legendaInfoPluvObservada2MesesAAnual,
	legendaInfoPluvObservadaPeriodoObservada,
} from '@componentes/mapa-paraiba-svg/legenda';
import { PluviometriaObservadaCores } from '../../../enum/variaveis-meteorologicas-enum';
import { TipoEstacao } from '@modulos/meteorologia/submodulos/estacao/enums/tipo-estacao';
import { corrigeDuplicacaoNome } from '../../../utils/corrige-duplicacao-nome';
import { NumberUtils } from '@utils/number-utils';

@Component({
	selector: 'seira-mosaico-chuvas',
	templateUrl: './mosaico-chuvas.component.html',
	styleUrls: ['./mosaico-chuvas.component.scss'],
})
export class MosaicoDeChuvaComponent implements OnDestroy, InstanciaRelatorio {
	carregando = true;
	resultados: VariaveisMeteorologicasPorPeriodoETipoVariavel[] = [];
	periodoTitulo = '';
	funcaoCoresMapa?: (valor: number) => string;
	inputs = inject(INPUTS_RELATORIOS);

	descricaoRelatorio =
		'Define-se como mosaico de chuvas o conjunto de mapas gerados para representar a distribuição espacial da pluviometria acumulada em um período específico, seja por dia, mês ou ano.';

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: async () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			activeFakeLoading: true,
			onClick: () => this.exportarPDF(),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	variaveisMeteorologicasPorPeriodo: VariaveisMeteorologicasPorPeriodo[] = [];
	legenda: LegendaOptions = {
		label: LabelsHistoricoVariaveisMeteorologicas.PRECIPITACAO,
		items: [],
	};

	constructor(
		private relatorioService: RelatoriosService,
		private toastr: ToastrService
	) {
		this.setValidators();
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
	}

	gerarRelatorio() {
		const dataInicio: Moment = this.inputs.form.get(
			FormularioRelatorio.DATA_INICIO
		)?.value;
		const dataFim: Moment = this.inputs.form.get(FormularioRelatorio.DATA_FIM)
			?.value;
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;
		const dataInicioConsulta = this.serializarDataConsulta(dataInicio);
		const dataFimConsulta = this.serializarDataConsulta(dataFim);
		this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
			moment(dataInicio).toDate(),
			moment(dataFim).toDate(),
			periodo.toUpperCase()
		);

		if (dataInicio && dataFim && periodo.toUpperCase() == 'MENSAL') {
			this.inputs.setLoading(true);
			this.carregando = true;

			this.relatorioService
				.listarVariaveisMeteorologicas(
					periodo.toUpperCase(),
					dataInicioConsulta,
					dataFimConsulta,
					'PLUVIOMETRO_CONVENCIONAL'
				)
				.subscribe({
					next: res => {
						const final = new Date(dataFimConsulta);
						const dadosFiltrados = res.filter(item => {
							const dataItem = new Date(item.data);
							return dataItem <= final;
						});
						const resOrdenadoPorData = [...dadosFiltrados].sort(
							(a, b) => new Date(a.data).getTime() - new Date(b.data).getTime()
						);

						const collator = new Intl.Collator('pt-BR', {
							sensitivity: 'base',
						});

						const resConvencionais = resOrdenadoPorData.map(r => ({
							...r,
							historico: [...r.historico].sort((a, b) =>
								collator.compare(a.nomeMunicipio, b.nomeMunicipio)
							),
						}));

						this.relatorioService.atualizaVisibilidadeBotoesExportacao(
							resConvencionais
						);
						this.variaveisMeteorologicasPorPeriodo = resConvencionais;
						this._resultados = resConvencionais;
					},
					error: err => {
						this.toastr.error('Ocorreu um erro ao gerar o relatório', err);
						this.inputs.setLoading(false);
						this.carregando = false;
					},
					complete: () => {
						this.inputs.setLoading(false);
						this.carregando = false;
					},
				});
		} else if (periodo.toUpperCase() == 'ANUAL') {
			this.inputs.setLoading(true);
			this.carregando = true;
			this.relatorioService
				.listarVariaveisMeteorologicas(
					periodo.toUpperCase(),
					dataInicioConsulta,
					dataFimConsulta,
					'PLUVIOMETRO_CONVENCIONAL'
				)
				.subscribe({
					next: res => {
						const final = new Date(dataFimConsulta);
						const dadosFiltrados = res.filter(item => {
							const dataItem = new Date(item.data);
							return dataItem <= final;
						});
						const resOrdenadoPorData = [...dadosFiltrados].sort(
							(a, b) => new Date(a.data).getTime() - new Date(b.data).getTime()
						);
						const collator = new Intl.Collator('pt-BR', {
							sensitivity: 'base',
						});
						const resConvencionais = resOrdenadoPorData.map(r => ({
							...r,
							historico: [...r.historico].sort((a, b) =>
								collator.compare(a.nomeMunicipio, b.nomeMunicipio)
							),
						}));

						this.relatorioService.atualizaVisibilidadeBotoesExportacao(
							resConvencionais
						);
						this.variaveisMeteorologicasPorPeriodo = resConvencionais;
						this._resultados = resConvencionais;
					},
					error: err => {
						this.toastr.error('Ocorreu um erro ao gerar o relatório', err);
						this.inputs.setLoading(false);
						this.carregando = false;
					},
					complete: () => {
						this.inputs.setLoading(false);
						this.carregando = false;
					},
				});
		} else if (dataInicio && dataFim && periodo.toUpperCase() == 'DIARIO') {
			this.inputs.setLoading(true);
			this.carregando = true;
			this.relatorioService
				.listarVariaveisMeteorologicas(
					TiposPeriodosVariaveisMeteorologicas.DIARIO,
					dataInicioConsulta,
					dataFimConsulta,
					'PLUVIOMETRO_CONVENCIONAL'
				)
				.subscribe({
					next: variaveis => {
						const dadosOrdenados = [...variaveis].sort((a, b) => {
							const dateA = new Date(a.data);
							const dateB = new Date(b.data);
							return dateA.getTime() - dateB.getTime();
						});
						const collator = new Intl.Collator('pt-BR', {
							sensitivity: 'base',
							numeric: true,
						});
						dadosOrdenados.forEach(dia => {
							dia.historico.sort((a, b) => {
								return collator.compare(a.nomeMunicipio, b.nomeMunicipio);
							});
						});
						this.relatorioService.atualizaVisibilidadeBotoesExportacao(
							dadosOrdenados
						);
						this.variaveisMeteorologicasPorPeriodo = dadosOrdenados;
						this._resultados = dadosOrdenados;
					},
					error: err => {
						this.toastr.error('Ocorreu um erro ao gerar o relatório', err);
						this.inputs.setLoading(false);
						this.carregando = false;
					},
					complete: () => {
						this.inputs.setLoading(false);
						this.carregando = false;
					},
				});
		}
	}

	private serializarDataConsulta(data: Date | Moment): string {
		return moment(data).format('YYYY-MM-DD');
	}

	set _resultados(value: VariaveisMeteorologicasPorPeriodo[]) {
		this.legenda.label = LabelsHistoricoVariaveisMeteorologicas.PRECIPITACAO;

		this.resultados = value
			.map(el => this.resultadoPorTipoPeriodo(el))
			.filter(el => el.historico.length);
		this.setCoresLegenda();
	}

	getFormItemValue(formValue: string) {
		return this.inputs.form.get(formValue)?.value;
	}

	setCoresLegenda() {
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;
		this.legenda.items = [];
		const diaInicio = this.getFormItemValue('dataInicio');
		const diaFim = this.getFormItemValue('dataFim');
		const diffMeses = DateTimeUtils.getDiferencaMesesUTC(
			new Date(diaFim),
			new Date(diaInicio)
		);

		if (periodo === 'anual') {
			const valores = Object.keys(legendaInfoPluvObservada2MesesAAnual).map(e =>
				Number(e)
			);
			const valoresDiariosStrings = valores.map(valor =>
				new Intl.NumberFormat('pt-BR').format(valor)
			);
			const cores = Object.values(PluviometriaObservadaCores);
			this.configurarLegenda(valores, cores, valoresDiariosStrings);
		} else {
			const valoresDiarios = Object.keys(
				legendaInfoPluvObservadaPeriodoObservada
			)
				.map(Number)
				.filter(v => Number.isFinite(v))
				.sort((a, b) => a - b);

			const valoresDiariosString = valoresDiarios.map(valor =>
				new Intl.NumberFormat('pt-BR').format(valor)
			);
			const coresDiarias = Object.values(PluviometriaObservadaCores);
			this.configurarLegenda(
				valoresDiarios,
				coresDiarias,
				valoresDiariosString
			);
		}

		this.funcaoCoresMapa = (valor: number) => {
			for (let i = 0; i < this.legenda.items.length; i++) {
				const itemDaLegenda = this.legenda.items[i];
				const nextItem = this.legenda.items[i + 1];
				if (nextItem) {
					if (
						valor >= Number(itemDaLegenda.numberCurrentAndNext) &&
						valor < Number(nextItem.numberCurrentAndNext)
					) {
						return itemDaLegenda.color;
					}
				} else {
					// Caso especial para o último item da legenda
					if (valor >= Number(itemDaLegenda.numberCurrentAndNext)) {
						return itemDaLegenda.color;
					}
				}
			}

			return ''; // Retorna vazio se o valor não estiver em nenhum intervalo
		};
	}

	private configurarLegenda(
		valores: number[],
		cores: string[],
		valoresString: string[]
	): void {
		this.legenda.items = valores.map((valor, index) => ({
			numberCurrentAndNext: valor,
			title: valoresString[index],
			color: cores[index],
		}));
	}

	resultadoPorTipoPeriodo(
		resultado: VariaveisMeteorologicasPorPeriodo
	): VariaveisMeteorologicasPorPeriodoETipoVariavel {
		const periodo: keyof typeof TiposPeriodosVariaveisMeteorologicas =
			this.inputs.form
				.get(FormularioRelatorio.PERIODO_BUSCA)
				?.value.toUpperCase();
		const tipoVariavel: ValuesVariaveis = ValuesVariaveis.PRECIPITACAO;

		const agrupadoPorGeocodigo: Record<
			number,
			{
				valores: number[];
				nomeEstacao: string;
				tipoEstacao: keyof typeof TipoEstacao;
				nomeMunicipio: string;
			}
		> = {};

		resultado.historico.forEach(el => {
			if (isNotNuloOuUndefined(el['precipitacaoAcumulada'])) {
				const geocodigo = Number(el.geocodigo);

				if (!agrupadoPorGeocodigo[geocodigo]) {
					agrupadoPorGeocodigo[geocodigo] = {
						valores: [],
						nomeEstacao: el.nomeEstacao,
						tipoEstacao: el.tipoEstacao as keyof typeof TipoEstacao,
						nomeMunicipio: el.nomeMunicipio,
					};
				}

				agrupadoPorGeocodigo[geocodigo].valores.push(
					el['precipitacaoAcumulada']
				);
			}
		});

		const historicoComMedia = Object.keys(agrupadoPorGeocodigo).map(
			geocodigoStr => {
				const geocodigo = Number(geocodigoStr);
				const dados = agrupadoPorGeocodigo[geocodigo];
				const soma = dados.valores.reduce((acc, valor) => acc + valor, 0);

				return {
					periodo,
					geocodigo,
					valor: soma,
					nomeEstacao: dados.nomeEstacao,
					tipoEstacao: dados.tipoEstacao,
					nomeMunicipio: dados.nomeMunicipio,
				};
			}
		);

		return {
			tipoVariavel,
			data: resultado.data,
			historico: historicoComMedia,
		};
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
	}

	getDadosTabelaParaExportacao(
		dados: VariaveisMeteorologicasPorPeriodoETipoVariavel[],
		isPdf: boolean
	) {
		const tableData: any[][] = [];

		const colunas = [
			{ text: 'Município', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Tipo da estação', fillColor: '#DCDCDC', alignment: 'center' },
			{
				text: 'Pluviometria observada (mm)',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{ text: 'Data', fillColor: '#DCDCDC', alignment: 'center' },
		];

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;
		const formatoData =
			periodo.toUpperCase() === 'ANUAL'
				? 'yyyy'
				: periodo.toUpperCase() === 'MENSAL'
				? 'MM/yyyy'
				: 'dd/MM/yyyy';

		dados.forEach((item: VariaveisMeteorologicasPorPeriodoETipoVariavel) => {
			item.historico.forEach(historico => {
				const rowData: any[] = [
					corrigeDuplicacaoNome(historico.nomeMunicipio),

					alinharDado(
						historico.tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL'
							? 'PCO'
							: historico.tipoEstacao,
						isPdf
					),
				];
				rowData.push(
					alinharDado(
						numberToBrNumberTables(historico.valor, 1),
						isPdf
					),
					alinharDado(
						DateTimeUtils.formatarData(item.data.toString(), formatoData),
						isPdf
					)
				);

				tableData.push(rowData);
			});
		});

		return tableData;
	}

	async exportarPDF() {
		const pdfDefinitions: any = await pdfseira.documentDefinitions();

		const promiseImagens = this.resultados.map(async (historico, index) => {
			const imagem: any = document.getElementById('mapa-' + index + '-imagem');

			if (imagem) {
				const canvas = document.createElement('canvas');
				canvas.width = imagem.width * 1.5;
				canvas.height = imagem.height * 1.4;
				canvas.style.background = 'color: black';
				canvas?.getContext('2d')?.drawImage(imagem, 0, 0);

				const url = canvas.toDataURL('image/png', 1);
				const imageData = await getBase64ImageFromURL(url);
				return { imagem: imageData, historico };
			}

			return null;
		});
		const legendaHtml = document.getElementById('legenda');
		if (legendaHtml) {
			const img = await html2canvas(legendaHtml, {
				useCORS: true,
				allowTaint: true,
				logging: false,
				scale: 2,
			}).then(canvas => canvas.toDataURL('image/png', 1));
			pdfDefinitions.content.push({
				text: `Mosaico de chuvas - ${this.periodoTitulo}`,
				alignment: 'center',
			});

			pdfDefinitions.content.push({
				image: img,
				margin: [0, 10, 0, 10],
				alignment: 'center',
				width: 500,
			});
		}

		Promise.all(promiseImagens).then(imagens => {
			if (imagens.every(imagem => imagem === null)) {
				this.toastr.error(
					'Não é possível gerar um pdf pois nenhum mapa foi gerado.'
				);
				return;
			}

			const columns: any = [[], [], []]; // Dividindo em 3 colunas

			const formatData = (data: Date, periodo: string): string => {
				switch (periodo.toUpperCase()) {
					case 'ANUAL':
						return moment(data).format('yyyy').toString();
					case 'MENSAL':
						return moment(data).format('MM/yyyy').toString();
					case 'DIARIO':
						return moment(data).format('DD/MM/yyyy').toString();
					default:
						return 'MM/yyyy';
				}
			};

			imagens.forEach((resultadoProcessamentoDaImagem, index) => {
				if (resultadoProcessamentoDaImagem) {
					const dataFormatada = formatData(
						resultadoProcessamentoDaImagem.historico.data,
						resultadoProcessamentoDaImagem.historico.historico[0].periodo
					);

					const dateText = {
						text: dataFormatada,
						fontSize: 12,
						alignment: 'center',
						margin: [0, 10],
					};
					const image = {
						image: resultadoProcessamentoDaImagem.imagem,
						alignment: 'center',
						width: 160,
					};

					columns[index % 3].push(image);
					columns[index % 3].push(dateText);
				}
				if ((index < 15 && index + 1 === 15) || (index - 14) % 18 === 0) {
					columns[0].push({ text: '', pageBreak: 'after' });
					columns[1].push({ text: '', pageBreak: 'after' });
					columns[2].push({ text: '', pageBreak: 'after' });
				}
			});

			pdfDefinitions.content.push({
				columns: columns.map((column: any) => ({
					width: '33.3%',
					stack: column,
				})),
			});

			const tableData = this.getDadosTabelaParaExportacao(
				this.resultados,
				true
			);

			pdfDefinitions.content.push(
				{
					text: '',
					alignment: 'center',
					fontSize: 10,
				},
				{
					text: 'PCO: Pluviômetro Convencional.',
					fontSize: 9,
					margin: [0, 5, 70, 10],
				},
				{
					table: {
						widths: ['25%', '25%', '25%', '25%'],
						body: tableData,
						layout: {
							alignment: 'center',
							noWrap: false,
						},
					},

					fontSize: 9,
					marginLeft: 0,
				}
			);

			baixarEAbrirPDF(
				`Mosaico de Chuva - ${this.periodoTitulo}`,
				pdfDefinitions
			);
		});
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(
			this.resultados,
			false
		);

		DocumentExporter.gerarCSV(
			tableData,
			`Mosaico de Chuva - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(
			this.resultados,
			false
		);

		let txtData = '';
		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Mosaico de Chuva - ${this.periodoTitulo}`
		);
	}

	getMosaicosChuva(): Array<{
		data: string | number | Date;
		precipitacaoAcumulada: number | string;
		nomeEstacao: string;
		nomeMunicipio: string;
	}> {
		const mosaicos: Array<{
			data: string | number | Date;
			precipitacaoAcumulada: number | string;
			nomeEstacao: string;
			nomeMunicipio: string;
			tipoEstacao: string;
		}> = [];

		this.resultados.map(mosaico => {
			const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
				?.value;

			const formatoData =
				periodo.toUpperCase() === 'ANUAL'
					? 'yyyy'
					: periodo.toUpperCase() === 'MENSAL'
					? 'MM/yyyy'
					: 'dd/MM/yyyy';

			for (let index = 0; index < mosaico.historico.length; index++) {
				let dataFormatada: string | number = DateTimeUtils.formatarData(
					mosaico.data.toString(),
					formatoData
				);
				if (formatoData === 'yyyy') {
					dataFormatada = Number(dataFormatada);
				}
				mosaicos.push({
					nomeMunicipio: corrigeDuplicacaoNome(
						mosaico.historico[index].nomeMunicipio
					),
					precipitacaoAcumulada: isNuloOuUndefined(
						mosaico.historico[index].valor
					)
						? '-'
						: NumberUtils.getIgnorandoCasasDecimais(
								mosaico.historico[index].valor
						  ),
					nomeEstacao: mosaico.historico[index].nomeEstacao,
					tipoEstacao:
						mosaico.historico[index].tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL'
							? 'PCO'
							: mosaico.historico[index].tipoEstacao,
					data: dataFormatada,
				});
			}
		});
		mosaicos.sort((a, b) => {
			const NomeA = a.nomeMunicipio
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
			const NomeB = b.nomeMunicipio
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
			if (NomeA < NomeB) {
				return -1;
			}
			if (NomeA > NomeB) {
				return 1;
			}
			return 0;
		});

		return mosaicos;
	}

	async exportarXLSX() {
		const nomeArquivo = `Mosaico de Chuva - ${this.periodoTitulo}`;

		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};

		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers = [
			{
				label: 'nomeMunicipio',
				text: 'Município',
				size: 48,
			},
			{ label: 'tipoEstacao', text: 'Tipo da estação', size: 24 },
			{
				label: 'precipitacaoAcumulada',
				text: 'Pluviometria observada (mm)',
				size: 24,
			},
			{ label: 'data', text: 'Data', size: 24 },
		];

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex > 0) {
								return 'rowStyleCenter';
							} else {
								return 'rowStyle';
							}
						}
					},
					headers: headers,
					data: this.getMosaicosChuva(),
					columns: [],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: `${nomeArquivo}`,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	private downloadDataUrl(nomeArquivoSemExt: string, dataUrl: string) {
		const a = document.createElement('a');
		a.href = dataUrl;
		a.download = `${nomeArquivoSemExt}.png`;
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}

	async exportarPNG() {
		// aguarde um pouco para garantir que as imagens do DOM renderizem
		await new Promise(r => setTimeout(r, 200));

		// helper para carregar imagens (incluindo a logo da AESA)
		const carregarImagem = (src: string): Promise<HTMLImageElement> => {
			return new Promise((resolve, reject) => {
				const image = new Image();
				image.crossOrigin = 'anonymous';
				image.onload = () => resolve(image);
				image.onerror = err => reject(err);
				image.src = src;
			});
		};

		// -------- 1) LEGENDA
		let legendaImg: HTMLImageElement | null = null;
		const legendaHtml = document.getElementById('legenda');
		if (legendaHtml) {
			const legendaDataUrl = await html2canvas(legendaHtml, {
				useCORS: true,
				allowTaint: true,
				logging: false,
				scale: 2,
			}).then(c => c.toDataURL('image/png', 1));

			legendaImg = await carregarImagem(legendaDataUrl);
		}

		// -------- 2) IMAGENS DO MOSAICO + FORMATADOR DE DATA
		const formatData = (data: Date, periodo: string): string => {
			switch ((periodo || '').toUpperCase()) {
				case 'ANUAL':
					return moment(data).format('yyyy').toString();
				case 'MENSAL':
					return moment(data).format('MM/yyyy').toString();
				case 'DIARIO':
					return moment(data).format('DD/MM/yyyy').toString();
				default:
					return moment(data).format('MM/yyyy').toString();
			}
		};

		type Item = {
			img: HTMLImageElement;
			dataStr: string;
			w: number;
			h: number;
		};
		const itens: Item[] = [];

		for (let i = 0; i < this.resultados.length; i++) {
			const el = document.getElementById(
				'mapa-' + i + '-imagem'
			) as HTMLImageElement | null;
			if (!el) continue;

			const img = await carregarImagem(el.src);

			const dataStr = formatData(
				this.resultados[i].data as unknown as Date,
				this.resultados[i].historico[0]?.periodo ?? 'MENSAL'
			);

			itens.push({ img, dataStr, w: img.naturalWidth, h: img.naturalHeight });
		}

		if (itens.length === 0) {
			this.toastr?.error('Não há mapas renderizados para exportar em PNG.');
			return;
		}

		const COLS = 3;
		const PADDING = 20;
		const GAP = 16;
		const CAPTION_H = 24;
		const TARGET_W = 320;

		const rows: Item[][] = [];
		for (let i = 0; i < itens.length; i += COLS) {
			rows.push(itens.slice(i, i + COLS));
		}

		const scaledRows = rows.map(row =>
			row.map(it => {
				const scale = TARGET_W / it.w;
				const h = Math.round(it.h * scale);
				return { ...it, w: TARGET_W, h };
			})
		);

		const rowHeights = scaledRows.map(row => {
			const maxH = row.reduce((m, it) => Math.max(m, it.h), 0);
			return maxH + CAPTION_H;
		});

		const width = PADDING * 2 + COLS * TARGET_W + (COLS - 1) * GAP;

		let legendaDrawWidth = 0,
			legendaDrawHeight = 0;
		if (legendaImg) {
			if (legendaImg.width > width - PADDING * 2) {
				const scale = (width - PADDING * 2) / legendaImg.width;
				legendaDrawWidth = Math.round(legendaImg.width * scale);
				legendaDrawHeight = Math.round(legendaImg.height * scale);
			} else {
				legendaDrawWidth = legendaImg.width;
				legendaDrawHeight = legendaImg.height;
			}
		}

		const topBlock = legendaImg ? legendaDrawHeight + GAP : 0;

		const height =
			PADDING * 2 +
			topBlock +
			rowHeights.reduce((a, b) => a + b, 0) +
			(rowHeights.length - 1) * GAP;

		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext('2d')!;
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, width, height);

		if (legendaImg) {
			const x = Math.round((width - legendaDrawWidth) / 2);
			ctx.drawImage(
				legendaImg,
				x,
				PADDING,
				legendaDrawWidth,
				legendaDrawHeight
			);
		}

		let y = PADDING + topBlock;
		ctx.font = '14px Arial';
		ctx.fillStyle = '#000';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'top';

		scaledRows.forEach((row, r) => {
			row.forEach((it, c) => {
				const x = PADDING + c * (TARGET_W + GAP);
				const yImg = y;
				ctx.drawImage(it.img, x, yImg, it.w, it.h);
				ctx.fillText(it.dataStr, x + it.w / 2, yImg + it.h + 6);
			});
			y += rowHeights[r] + GAP;
		});

		try {
			const logoAesa = await carregarImagem('assets/images/logos/aesa.png');

			const margem = width * 0.01;
			const larguraLogo = width * 0.05;
			const alturaLogo = (larguraLogo / logoAesa.width) * logoAesa.height;

			const xLogo = width - larguraLogo - margem;
			const yLogo = height - alturaLogo - margem;

			ctx.drawImage(logoAesa, xLogo, yLogo, larguraLogo, alturaLogo);
		} catch (e) {
			console.error('[Export PNG] Erro ao carregar logo da AESA:', e);
		}

		const pngUrl = canvas.toDataURL('image/png', 1);
		const nome = `Mosaico de Chuva - ${this.periodoTitulo}`;
		this.downloadDataUrl(nome, pngUrl);
	}
}
